import { plural } from '~/utils/display'

/**
 * Returns a relative time string for UI like in the Warpcast client.
 * Works for both past and future dates.
 
 * @param {number|string} ms - timestamp in milliseconds
 * @returns {string} - relative time string
 */
export const relativeTimeDiff = (
  ms: number | string,
  options?: {
    suffix?: string
    abbreviate?: boolean
  },
) => {
  const t = new Date(ms).getTime()
  if (Number.isNaN(t)) return ''

  const now = new Date().getTime()

  // Calculate the absolute difference in milliseconds
  const diffInMs = Math.abs(now - t)

  // Convert milliseconds to time units
  const minutes = Math.floor(diffInMs / 60000)
  const hours = Math.floor(diffInMs / 3600000)
  const days = Math.floor(diffInMs / (3600000 * 24))
  const weeks = Math.floor(days / 7)
  const months = Math.floor(days / 30)
  const years = Math.floor(days / 365)

  // Generate the relative time string based on the difference
  if (years > 0)
    return getTimeString(
      years,
      options?.abbreviate ? 'y' : plural(' year', years),
      options?.suffix,
    )
  if (months > 0 && months < 13)
    return getTimeString(
      months,
      options?.abbreviate ? 'mo' : plural(' month', months),
      options?.suffix,
    )
  if (weeks > 0 && weeks < 5)
    return getTimeString(
      weeks,
      options?.abbreviate ? 'w' : plural(' week', weeks),
      options?.suffix,
    )
  if (days > 0 && days < 7)
    return getTimeString(
      days,
      options?.abbreviate ? 'd' : plural(' day', days),
      options?.suffix,
    )
  if (hours > 0 && hours < 24)
    return getTimeString(
      hours,
      options?.abbreviate ? 'h' : plural(' hour', hours),
      options?.suffix,
    )
  if (minutes > 0 && minutes < 60)
    return getTimeString(
      minutes,
      options?.abbreviate ? 'm' : plural(' minute', minutes),
      options?.suffix,
    )

  return 'Just now'
}

const getTimeString = (value: number, unit: string, suffix?: string) => {
  return `${value}${unit}${suffix ? ` ${suffix}` : ''}`
}
